import { Plus, Trash2 } from "lucide-react";

import { NumericStepper } from "@/components/common/NumericStepper";
import { SelectField } from "@/components/common/SelectField";
import { unitLabel } from "@/components/common/UnitPicker";
import { Button } from "@/components/ui/button";
import type { Provision } from "@/features/provisions/types";

export type IngredientLine = {
  key: string;
  provisionId: string;
  quantity: string;
};

export const emptyIngredientLine = (): IngredientLine => ({
  key: crypto.randomUUID(),
  provisionId: "",
  quantity: "",
});

/** Same metrics as Input, for the plain number field on each line. */
/** Lines that carry both a matter and a quantity — the rest are just blanks. */
export function filledIngredients(lines: IngredientLine[]): Array<{ provisionId: string; quantity: number }> {
  return lines
    .filter((line) => line.provisionId && parseFloat(line.quantity) > 0)
    .map((line) => ({ provisionId: line.provisionId, quantity: parseFloat(line.quantity) }));
}

/**
 * Shared by the recipe form and by the product form that defines its recipe
 * inline, so a recipe is edited the same way wherever it is reached from.
 */
export function IngredientLines({
  lines,
  onChange,
  provisions,
}: {
  lines: IngredientLine[];
  onChange: (lines: IngredientLine[]) => void;
  provisions: Provision[];
}) {
  const updateLine = (key: string, patch: Partial<IngredientLine>) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="hidden grid-cols-[1fr_140px_44px] gap-3 bg-muted/60 px-4 py-3 text-[13px] font-semibold text-muted-foreground md:grid">
        <span>Matière première</span>
        <span>Quantité</span>
        <span />
      </div>

      {lines.map((line, index) => {
        const provision = provisions.find((item) => item.id === line.provisionId);
        const lineLabel = `ligne ${index + 1}${provision ? ` — ${provision.name}` : ""}`;
        return (
          <div
            key={line.key}
            className="grid gap-3 border-t border-muted px-4 py-3 md:grid-cols-[1fr_140px_44px] md:items-center"
          >
            <SelectField
              value={line.provisionId}
              onChange={(provisionId) => updateLine(line.key, { provisionId })}
              placeholder="Choisir une matière…"
              aria-label={`Matière première, ${lineLabel}`}
              options={provisions.map((item) => ({
                value: item.id,
                label: item.name,
                hint: `en ${unitLabel(item.measurementUnit)}`,
              }))}
            />

            <NumericStepper
              min={0}
              step={0.01}
              value={line.quantity}
              onChange={(value) => updateLine(line.key, { quantity: String(value) })}
              placeholder="0"
              aria-label={`Quantité${provision ? ` en ${unitLabel(provision.measurementUnit)}` : ""}, ${lineLabel}`}
              suffix={provision ? unitLabel(provision.measurementUnit) : undefined}
            />

            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Retirer la ${lineLabel}`}
              disabled={lines.length === 1}
              onClick={() => onChange(lines.filter((item) => item.key !== line.key))}
            >
              <Trash2 className="text-muted-foreground" />
            </Button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => onChange([...lines, emptyIngredientLine()])}
        className="flex w-full items-center gap-2 border-t border-muted px-4 py-3 text-[15px] font-medium transition hover:bg-muted"
      >
        <Plus size={18} />
        Ajouter une matière
      </button>
    </div>
  );
}
